import React, { useEffect, useRef, useState } from 'react';
import { apiHandler } from '../../../api/apihandler';
import { PEOPLE_YOU_MAY_KNOW, PROFILE_SEARCH, USER_FOLLOW_REQUEST } from '../../../api/constants';
import { apiSucceeded, getAvatarColor, getInitials } from '../feed/feedUtils';
import MyStatsWidget from '../../../components/nemonto/MyStatsWidget';
import PromoCard from '../../../components/nemonto/PromoCard';
import EmptyState from '../../../components/nemonto/EmptyState';
import './social-list-page.css';

const SEARCH_DEBOUNCE_MS = 400;

const PersonAvatar = ({ photo, name }) => {
  if (photo) return <img src={photo} alt={name} className="nmsl-avatar" />;
  return (
    <div className="nmsl-avatar" style={{ background: getAvatarColor(name || 'User') }}>
      {getInitials(...(name || 'User').split(' '))}
    </div>
  );
};

const PeopleYouMayKnowPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [busyIds, setBusyIds] = useState(new Set());
  const [searchText, setSearchText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Guards against an older, slower request overwriting a newer one's
  // results when the user keeps typing (e.g. "Swa" resolves after "Swapan").
  const requestIdRef = useRef(0);

  async function loadPage(pageNo, replace, term) {
    setLoading(true);
    const requestId = ++requestIdRef.current;
    try {
      const trimmedTerm = term.trim();
      const res = trimmedTerm
        ? await apiHandler(PROFILE_SEARCH, 'POST', { page: pageNo, limit: 20, search_text: trimmedTerm })
        : await apiHandler(PEOPLE_YOU_MAY_KNOW, 'POST', { page: pageNo, limit: 20 });

      if (requestId !== requestIdRef.current) return;

      if (apiSucceeded(res)) {
        const list = res?.data?.data?.users?.data || [];
        const meta = res?.data?.data?.users?.meta;
        setUsers((prev) => (replace ? list : [...prev, ...list]));
        setHasMore(!!meta && meta.current_page < meta.last_page);
        setPage(pageNo);
      } else if (replace) {
        setUsers([]);
        setHasMore(false);
      }
    } catch (e) {
      console.error(e);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }

  useEffect(() => { loadPage(1, true, ''); }, []);

  // Debounce keystrokes so we're not firing a search request per character.
  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(searchText), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => { loadPage(1, true, searchTerm); }, [searchTerm]);

  async function toggleFollow(userId, index, currentlyFollowing) {
    if (busyIds.has(userId)) return;
    setBusyIds((prev) => new Set([...prev, userId]));
    try {
      const res = await apiHandler(USER_FOLLOW_REQUEST, 'POST', { follower_id: userId });
      if (apiSucceeded(res)) {
        setUsers((prev) => {
          const copy = [...prev];
          copy[index] = { ...copy[index], is_following: currentlyFollowing ? 0 : 1 };
          return copy;
        });
      }
    } catch (e) { console.error(e); }
    finally {
      setBusyIds((prev) => { const next = new Set(prev); next.delete(userId); return next; });
    }
  }

  const isSearching = searchTerm.trim().length > 0;

  return (
    <div className="nmsl-layout">
      <div className="nmsl-main">
        <div className="nmsl-page-header">
          <div className="nmsl-header">
            <h3 className="nmsl-title">People You May Know</h3>
            {users.length > 0 && <span className="nmsl-count-pill">{users.length}</span>}
          </div>
          <p className="nmsl-subtitle">Cooks and creators with a taste for what you like.</p>

          <div className="nmsl-search-wrap">
            <span className="material-symbols-outlined nmsl-search-icon">search</span>
            <input
              type="text"
              className="nmsl-search-input"
              placeholder="Search by name…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              aria-label="Search people"
            />
            {searchText && (
              <button
                type="button"
                className="nmsl-search-clear"
                onClick={() => setSearchText('')}
                aria-label="Clear search"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            )}
          </div>
        </div>

        <div className="nmsl-main-body">
          {loading && users.length === 0 && <p className="nmsl-loading">{isSearching ? 'Searching…' : 'Loading…'}</p>}
          {!loading && users.length === 0 && isSearching && (
            <EmptyState icon="person_search" title="No matches found" sub={`No one named "${searchTerm.trim()}" was found.`} />
          )}
          {!loading && users.length === 0 && !isSearching && (
            <EmptyState icon="person_search" title="No suggestions right now" sub="Check back soon for more cooks to follow." />
          )}

          {users.length > 0 && (
            <div className="nmsl-grid">
              {users.map((u, index) => {
                const name = `${u?.first_name || ''} ${u?.last_name || ''}`.trim() || 'User';
                const isFollowing = u?.is_following === 1;
                return (
                  <div key={u.id} className="nmsl-card">
                    <PersonAvatar photo={u?.user_setting?.photo} name={name} />
                    <div className="nmsl-body">
                      <div className="nmsl-name">{name}</div>
                      <div className="nmsl-sub">{u?.follower_count ?? 0} followers</div>
                    </div>
                    <button
                      type="button"
                      className={`nmsl-follow-btn ${isFollowing ? 'nmsl-following' : ''}`}
                      disabled={busyIds.has(u.id)}
                      onClick={() => toggleFollow(u.id, index, isFollowing)}
                    >
                      {isFollowing ? 'Following' : 'Follow'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {hasMore && (
            <div className="nmsl-load-more-wrap">
              <button type="button" className="nmsl-load-more-btn" onClick={() => loadPage(page + 1, false, searchTerm)} disabled={loading}>
                {loading ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      </div>

      <aside className="nmsl-rail">
        <PromoCard
          label="Made it"
          title="See what everyone's cooking"
          sub="Browse real attempts from the community and get inspired for your next dish."
          ctaText="Explore Made It"
          to="/dashboard/app/profile"
          state={{ tab: 'madeit' }}
        />
        <MyStatsWidget />
      </aside>
    </div>
  );
};

export default PeopleYouMayKnowPage;
